import React, { useState, useEffect } from 'react';
import { api, AdminUserPublic } from '../../utils/api';
import { Organisation } from '../../types';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

export const PartnerManagement: React.FC = () => {
  const [partners, setPartners] = useState<AdminUserPublic[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create partner form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [users, orgs] = await Promise.all([api.getAdminUsers(), api.getOrganisations()]);
      setPartners(users.filter((u) => u.role === 'partner'));
      setOrganisations(orgs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formEmail.trim() || !formPassword.trim()) return;
    if (selectedOrgIds.length === 0) {
      setError('Partner must be assigned to at least one organisation');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await api.createAdminUser({
        name: formName.trim(),
        email: formEmail.trim(),
        password: formPassword.trim(),
        role: 'partner',
        orgAssignments: selectedOrgIds.map((orgId) => ({
          orgId,
          orgRole: 'org_full_admin' as const,
        })),
      });
      setFormName('');
      setFormEmail('');
      setFormPassword('');
      setSelectedOrgIds([]);
      setShowCreateForm(false);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create partner');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await api.deleteAdminUser(id);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete partner');
    }
  };

  const handleToggleActive = async (partner: AdminUserPublic) => {
    try {
      await api.updateAdminUser(partner.id, { isActive: !partner.isActive });
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update partner');
    }
  };

  const toggleOrgSelection = (orgId: string) => {
    setSelectedOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const getOrgName = (orgId: string) => {
    const org = organisations.find((o) => o.id === orgId);
    return org?.name || orgId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-base-content mb-2">Partner Management</h1>
            <p className="text-base-content/70">
              Manage partner users who can access and manage assigned organisations.
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn btn-primary gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            New Partner
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="btn btn-sm btn-ghost">
            Dismiss
          </button>
        </div>
      )}

      {/* Create Partner Form */}
      {showCreateForm && (
        <div className="bg-base-100 shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-base-content mb-4">Create Partner</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="input input-bordered"
                  placeholder="Partner name"
                  required
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="input input-bordered"
                  placeholder="partner@example.com"
                  required
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Password</span>
                </label>
                <input
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="input input-bordered"
                  placeholder="Min 8 characters"
                  minLength={8}
                  required
                />
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Assign Organisations</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {organisations.map((org) => (
                  <label
                    key={org.id}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedOrgIds.includes(org.id)
                        ? 'border-primary bg-primary/10'
                        : 'border-base-300 hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.includes(org.id)}
                      onChange={() => toggleOrgSelection(org.id)}
                      className="checkbox checkbox-primary checkbox-sm"
                    />
                    <div>
                      <p className="text-sm font-medium">{org.name}</p>
                      <p className="text-xs text-base-content/60">{org.billingEmail}</p>
                    </div>
                  </label>
                ))}
              </div>
              {organisations.length === 0 && (
                <p className="text-sm text-base-content/60 mt-2">
                  No organisations available. Create organisations first.
                </p>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" disabled={creating} className="btn btn-primary">
                {creating ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  'Create Partner'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Partners Table */}
      <div className="bg-base-100 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Organisations</th>
                <th>MFA</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-base-content/60 py-8">
                    No partners yet. Click "New Partner" to create one.
                  </td>
                </tr>
              ) : (
                partners.map((partner) => (
                  <tr key={partner.id} className="hover">
                    <td className="font-medium">{partner.name}</td>
                    <td className="text-sm">{partner.email}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {partner.orgAccess.map((access) => (
                          <span key={access.orgId} className="badge badge-sm badge-outline">
                            {getOrgName(access.orgId)}
                          </span>
                        ))}
                        {partner.orgAccess.length === 0 && (
                          <span className="text-xs text-base-content/50">None</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge badge-sm ${
                          partner.mfaEnabled ? 'badge-success' : 'badge-warning'
                        }`}
                      >
                        {partner.mfaEnabled ? 'Enabled' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleToggleActive(partner)}
                        className={`badge badge-sm cursor-pointer ${
                          partner.isActive ? 'badge-success' : 'badge-error'
                        }`}
                      >
                        {partner.isActive ? 'Active' : 'Disabled'}
                      </button>
                    </td>
                    <td className="text-sm text-base-content/70">
                      {new Date(partner.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(partner.id)}
                        className="btn btn-ghost btn-sm btn-square text-error"
                        title="Delete partner"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
